import React, { Component, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import pusher from "../../notifications/Pusher";
import CookieConsentBanner from "../common/CookieConsentBanner";
import { Loader } from "../common/loader";
import { Button } from "../common/customDesignedComponents/button";
import { ensureCallPilotChatbotReady } from "../helpers/callPilotChatbot";
import { getObjLength, servicerExitLoginUrl } from "../helpers/utils";
import { userProfileAction } from "../../redux/slices/user/userProfileSlice";
import { notificationActions } from "../../redux/slices/notification/notificationSlice";
import { serviceProviderProfileAction } from "../../redux/slices/serviceProvider/servicerProfile/servicerProfileSlice";
import { ServicerDashboardHeader } from "./ServicerDashboardHeader";
import { ServicerDashboardSidebar } from "./ServicerDashboardSidebar";

import "../../tailwind.css";

class ServicerLayoutErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background tailwind_component p-6">
          <div className="max-w-lg mx-auto rounded-xl border border-border bg-card p-6 shadow-sm">
            <h1 className="text-lg font-semibold text-foreground mb-2">
              Unable to load this page
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              Refresh the page or sign in again if the problem continues.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ServicerDashboardLayout = ({ comp }: { comp: React.ReactNode }) => {
  const dispatch = useDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [openAlert, setOpenAlert] = useState(true);
  const [pusherChannel, setPusherChannel] = useState<any>(null);
  const [isBelow699, setIsBelow699] = useState(false);

  const { servicerProfileDetails } = useSelector(
    (state: any) => state?.servicerProfile,
  );
  const { reRenderCount } = useSelector((state: any) => state?.userProfile);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 698px)");
    const apply = () => {
      setIsBelow699(mq.matches);
      if (mq.matches) {
        setIsSidebarOpen(false);
      }
    };
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Every servicer page remounts this layout. Reuse the signed-in user's profile
  // once it is loaded; a full refresh still loads it fresh.
  const hasCurrentProfile =
    servicerProfileDetails?.success === true &&
    Boolean(servicerProfileDetails?.data?.user_id) &&
    String(servicerProfileDetails.data.user_id) === localStorage.getItem("uID");

  useEffect(() => {
    if (hasCurrentProfile) return;
    dispatch(
      serviceProviderProfileAction.getServiceProviderProfile({
        url: "service-provider/profile?source=service_provider",
        data: {},
        token: true,
      }),
    );
  }, [dispatch, hasCurrentProfile]);

  useEffect(() => {
    void ensureCallPilotChatbotReady({ hideLauncher: true });
  }, []);

  useEffect(() => {
    if (servicerProfileDetails?.data?.user_id && pusherChannel === null) {
      const channel = pusher
        .subscribe(`App.User.${servicerProfileDetails.data.user_id}`)
        .unbind("equity_protect")
        .bind("equity_protect", (data: any) => {
          dispatch(
            notificationActions.appendLiveNotifications({
              ...data,
              read_at: null,
            }),
          );
          dispatch(notificationActions.setIsNewNotificationArrive(true));
        });
      setPusherChannel(channel);
    }
  }, [dispatch, pusherChannel, servicerProfileDetails?.data?.user_id]);

  useEffect(() => {
    if (
      getObjLength(servicerProfileDetails) &&
      servicerProfileDetails?.success &&
      servicerProfileDetails?.data?.status === "inactive"
    ) {
      const loginUrl = servicerExitLoginUrl();
      localStorage.clear();
      localStorage.setItem("preference", "true");
      window.location.href = loginUrl;
    }
  }, [servicerProfileDetails]);

  const handleNavigate = () => {
    dispatch(userProfileAction.setReRenderCount(reRenderCount + 1));
    if (typeof window !== "undefined" && window.innerWidth < 699) {
      setIsSidebarOpen(false);
    }
  };

  const userPreference = (access: boolean) => {
    localStorage.setItem("preference", String(access));
    setOpenAlert(false);
  };

  const shouldRenderSidebar = !isBelow699 || isSidebarOpen;
  // Hold the page until the first profile answer. Showing it before the request
  // starts mounted it twice: once before the loader, once after.
  const isInitialProfileLoading = !getObjLength(servicerProfileDetails);

  return (
    <ServicerLayoutErrorBoundary>
      <div className="min-h-screen bg-background tailwind_component">
        {shouldRenderSidebar ? (
          <ServicerDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            onNavigate={handleNavigate}
          />
        ) : null}

        <ServicerDashboardHeader
          isSidebarOpen={isSidebarOpen}
          onMenuClick={() => setIsSidebarOpen((open) => !open)}
        />

        <div
          className={`pt-16 min-h-screen transition-all duration-200 ${
            isBelow699 ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
          }`}
          onClick={() => {
            if (typeof window !== "undefined" && window.innerWidth < 699) {
              setIsSidebarOpen(false);
            }
          }}
        >
          <div className="p-4 md:p-6" key={reRenderCount}>
            {isInitialProfileLoading ? (
              <div className="flex min-h-[40vh] items-center justify-center">
                <Loader open={true} />
              </div>
            ) : (
              comp
            )}
          </div>
        </div>

        <CookieConsentBanner
          isVisible={openAlert}
          onAccept={() => userPreference(true)}
          onDecline={() => userPreference(false)}
          userPreferences={
            localStorage.getItem("preference") === null ||
            localStorage.getItem("preference")?.length === 0
          }
          isTab={false}
          code="web"
        />
      </div>
    </ServicerLayoutErrorBoundary>
  );
};

export default ServicerDashboardLayout;
